import { normalizeSessionURL } from '@/data/trybookingEvents';

function slugify(value) {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function formatDisplayDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

function formatEventDateRange(start, end) {
    const startLabel = formatDisplayDate(start);
    if (!end) return startLabel;

    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return startLabel;

    const endLabel = formatDisplayDate(end);
    if (startLabel === endLabel) return startLabel;

    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const endShort = endDate.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: sameYear ? undefined : 'numeric',
    });

    return `${startLabel} – ${endShort}`;
}

function parseCityFromLocation(location) {
    if (!location) return '';

    const normalized = String(location).trim();
    const enDashParts = normalized.split('–').map((part) => part.trim());
    if (enDashParts.length > 1) return enDashParts[enDashParts.length - 1];

    const hyphenParts = normalized.split(' - ').map((part) => part.trim());
    if (hyphenParts.length > 1) return hyphenParts[hyphenParts.length - 1];

    return normalized;
}

export function buildEventSlug(event) {
    return `${slugify(event.title)}-${event.id}`;
}

export function parseEventIdFromSlug(slug) {
    const match = String(slug ?? '').match(/-(\d+)$/);
    if (match) return Number(match[1]);
    if (/^\d+$/.test(String(slug ?? ''))) return Number(slug);
    return null;
}

function formatVenueDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

function mapVenues(event) {
    return (event.sessions ?? []).map((session) => ({
        id: String(session.id),
        location: session.location,
        city: session.city ?? parseCityFromLocation(session.location),
        date: formatVenueDate(session.sessionDate),
        time: session.sessionTime ?? '',
        trybookingEventId: session.tryBookingID,
        trybookingSessionId: session.sessionID,
        ticketUrl: normalizeSessionURL(session.sessionURL, session.tryBookingID),
    }));
}

function collectFilterCities(event, venues, hasMultipleLocations) {
    if (hasMultipleLocations) {
        const cities = [...new Set(
            venues.map((venue) => venue.city).filter(Boolean),
        )];
        return cities.length ? cities : (event.city ? [event.city] : []);
    }
    return event.city ? [event.city] : [];
}

function formatCityLabel(cities, hasMultipleLocations) {
    if (!cities.length) return '';
    if (hasMultipleLocations && cities.length > 1) {
        return cities.join(', ');
    }
    return cities[0];
}

export function mapApiEvent(event) {
    const priceNumber = Number(event.price);
    const priceLabel = Number.isFinite(priceNumber)
        ? `From $${priceNumber % 1 === 0 ? priceNumber.toFixed(0) : priceNumber.toFixed(2)}`
        : `From $${event.price}`;

    const venues = mapVenues(event);
    const hasMultipleLocations = venues.length > 1 || Boolean(event.hasMultipleLocations && venues.length > 0);
    const filterCities = collectFilterCities(event, venues, hasMultipleLocations);
    const primaryLocation = venues[0]?.location ?? '';
    const venue = primaryLocation;
    const date = formatEventDateRange(event.bookingStartDateTime, event.bookingEndDateTime);
    const category = event.category ?? 'Music';
    const cityLabel = formatCityLabel(filterCities, hasMultipleLocations)
        || (hasMultipleLocations
            ? `${venues.length} locations across Australia`
            : parseCityFromLocation(primaryLocation));

    return {
        id: String(event.id),
        title: event.title,
        subtitle: event.isUpcomming ? `${category} · Upcoming` : category,
        slug: buildEventSlug(event),
        date,
        startsAt: event.bookingStartDateTime,
        endsAt: event.bookingEndDateTime,
        venue,
        city: cityLabel,
        filterCity: filterCities[0] ?? event.city ?? '',
        filterCities,
        category,
        image: event.imageURL,
        alt: event.title,
        ticketUrl: normalizeSessionURL(event.sessionURL, event.tryBookingID),
        trybookingEventId: event.tryBookingID,
        trybookingSessionId: event.sessionID,
        price: priceLabel,
        featured: Boolean(event.isTopEvent),
        isBanner: Boolean(event.isBanner),
        isUpcoming: Boolean(event.isUpcomming),
        eventStatus: event.eventStatus,
        description: event.about,
        sessionURL: normalizeSessionURL(event.sessionURL, event.tryBookingID),
        presenter: '',
        hasMultipleLocations,
        venues,
    };
}

export function isActiveEvent(event) {
    return event.eventStatus === 'active';
}

export function isEventBookingOpen(event) {
    const endMs = new Date(event.endsAt ?? event.startsAt).getTime();
    if (Number.isNaN(endMs)) return true;
    return endMs >= Date.now();
}

export function isUpcomingEvent(event) {
    if (event.isUpcoming === false) return false;
    if (event.isUpcoming !== true && !event.startsAt) return false;
    return isEventBookingOpen(event);
}

export function isPassedEvent(event) {
    return !isEventBookingOpen(event);
}

export function sortEventsByDate(events) {
    return [...events].sort(
        (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    );
}

export function sortPassedEventsByDate(events) {
    return [...events].sort(
        (a, b) => new Date(b.endsAt ?? b.startsAt).getTime() - new Date(a.endsAt ?? a.startsAt).getTime(),
    );
}

export function splitHomepageEvents(events) {
    const upcoming = sortEventsByDate(events.filter(isEventBookingOpen));
    const passed = sortPassedEventsByDate(events.filter(isPassedEvent));
    return { upcoming, passed };
}

export function sortEventsByDateWithExpiredLast(events) {
    return [...events].sort((a, b) => {
        const aExpired = !isEventBookingOpen(a);
        const bExpired = !isEventBookingOpen(b);
        if (aExpired !== bExpired) {
            return aExpired ? 1 : -1;
        }
        return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
    });
}
